import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { v4 as uuid } from 'uuid';
import {
  useAddQuizMutation,
  useEditQuizMutation,
  useGetQuizByIdQuery,
} from "../../../features/quiz/quizApi";
import { closeEditing } from "../../../features/quiz/quizSlice";
import {
  useAddVideoMutation,
  useGetVideosQuery,
} from "../../../features/videos/videosApi";
import { hasCorrectAnswer } from "../../../utils/hasCorrectAnswer";

const QuizModal = ({ open, control }) => {
  const dispatch = useDispatch();
  const [question, setQuestion] = useState("");
  const [video, setVideo] = useState({});
  const [options, setOptions] = useState([
    { id: 1, option: "", isCorrect: false },
    { id: 2, option: "", isCorrect: false },
    { id: 3, option: "", isCorrect: false },
    { id: 4, option: "", isCorrect: false },
  ]);

  const { quizId, editMode } = useSelector((state) => state.quiz.editing);

  // get videos array
  const { data: videos, isLoading } = useGetVideosQuery();

  // get quiz info for edit mode to populate form
  const {
    data: quiz,
    isLoading: quizReqLoading,
    refetch,
  } = useGetQuizByIdQuery(quizId);

  // hook for adding new quiz
  const [addQuiz, { isSuccess: createSuccess }] = useAddQuizMutation();

  // hook for edit quiz
  const [editQuiz, { isSuccess: editSuccess }] = useEditQuizMutation();

  // Populate form data
  useEffect(() => {
    if (quiz?.id) {
      const {
        question: initialQuestion,
        video_id,
        video_title,
        options: initialOptions,
      } = quiz;
      setVideo({ title: video_title, id: video_id });
      setQuestion(initialQuestion);
      setOptions(initialOptions);
    }
  }, [quiz, refetch]);

  useEffect(() => {
    if (createSuccess || editSuccess) {
      refetch();
    }
  }, [createSuccess, editSuccess]);

  const formReset = () => {
    setQuestion("");
    setVideo({});
    setOptions([
      { id: 1, option: "", isCorrect: false },
      { id: 2, option: "", isCorrect: false },
      { id: 3, option: "", isCorrect: false },
      { id: 4, option: "", isCorrect: false },
    ]);
  };

  const handleOptionChange = (index, event) => {
    const newOptions = [...options];
    newOptions[index].option = event.target.value;
    setOptions(newOptions);
  };

  // Function for selecting correct answer
  const handleRadioChange = (index) => {
    const newOptions = options.map((option, i) => {
      if (i === index) {
        return { ...option, isCorrect: true };
      } else {
        return { ...option, isCorrect: false };
      }
    });
    setOptions(newOptions);
  };

  //  function to change the corresponding video on select options
  const handleVideoChange = (e) => {
    const selectedOption = JSON.parse(e.target.value);
    setVideo(selectedOption);
  };

  const handleCreate = (e) => {
    e.preventDefault();

    if(hasCorrectAnswer(options)){
      const data = {
      id: uuid(),
      question,
      video_title: video.title,
      video_id: video.id,
      options,
    };

    addQuiz(data);
    closeModal()
  }else{
    toast.error("Please select the correct answer")
  }
  
  };

  const handleEdit = (e) => {
    e.preventDefault();

    const data = {
      question,
      video_title: video.title,
      video_id: video.id,
      options,
    };

    editQuiz({ data, id: quiz.id });
    closeModal()
    
  };

  const closeModal = () => {
    dispatch(closeEditing());
    formReset();
    control();
  };

  return (
    open && (
      <main className="bg-secondary flex flex-col mx-8 p-8 form-modal">
        <h1 className="mb-10 text-center lws-section-title mt-8 text-2xl">
          Add New Quiz
        </h1>
        <div className="max-w-md mx-auto mt-8">
          <form
            className="flex flex-col space-y-6"
            onSubmit={editMode ? handleEdit : handleCreate}
          >
            <div className="fieldContainer">
              <label>Question</label>
              <div className="flex border border-cyan rounded-md shadow-sm">
                <input
                  type="text"
                  required
                  className="bg-secondary w-full h-8 py-2 px-3 rounded-md form-input"
                  placeholder="Question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
              </div>
            </div>
            <div className="fieldContainer">
              <label>Video</label>
              <div className="flex border border-cyan rounded-md shadow-sm">
                <select
                  className="bg-secondary w-full h-8 px-3 rounded-md form-input"
                  required
                  value={JSON.stringify(video)}
                  onChange={handleVideoChange}
                >
                  <option value="" hidden selected>
                    Select Video
                  </option>

                  {videos.map((option) => {
                    const { id, title } = option;
                    return (
                      <option key={id} value={JSON.stringify({ title, id })}>
                        {title}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {options.map((option, index) => (
              <div className="quizOptionContainer" key={index}>
                <label>Option {index + 1}:</label>
                <div className="flex justify-between items-center">
                  <div className="flex border border-cyan rounded-md shadow-sm w-full">
                    <input
                      className="bg-secondary w-full h-8 py-2 px-3 rounded-md form-input"
                      type="text"
                      value={option.option}
                      onChange={(event) => handleOptionChange(index, event)}
                      required
                    />
                  </div>
                  <label className="correct flex">
                    <input
                      className="correct"
                      type="radio"
                      checked={option.isCorrect}
                      onChange={() => handleRadioChange(index)}
                      required
                    />
                    <svg
                      className="h-8"
                      fill="rgb(200, 227, 211)"
                      xmlns="http://www.w3.org/2000/svg"
                      shape-rendering="geometricPrecision"
                      text-rendering="geometricPrecision"
                      image-rendering="optimizeQuality"
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      viewBox="0 0 512 512"
                    >
                      <path
                        fill-rule="nonzero"
                        d="M256 0c70.69 0 134.69 28.66 181.02 74.98C483.34 121.3 512 185.31 512 256c0 70.69-28.66 134.69-74.98 181.02C390.69 483.35 326.69 512 256 512c-70.69 0-134.7-28.65-181.02-74.98C28.66 390.69 0 326.69 0 256c0-70.69 28.66-134.7 74.98-181.02C121.3 28.66 185.31 0 256 0zm-89.53 236.97c18 10.36 29.7 18.99 43.61 34.34 36.18-58.19 75.43-90.48 126.48-136.26l4.98-1.91h55.85c-74.89 83.19-132.94 151.75-184.96 252.08-27.05-57.92-51.19-97.84-105.16-134.87l59.2-13.38zm244.92-136.36C371.62 60.85 316.68 36.25 256 36.25c-60.69 0-115.63 24.6-155.39 64.36-39.76 39.77-64.36 94.7-64.36 155.39 0 60.68 24.6 115.62 64.36 155.39 39.76 39.76 94.7 64.36 155.39 64.36 60.68 0 115.62-24.6 155.39-64.36 39.76-39.77 64.36-94.71 64.36-155.39 0-60.69-24.6-115.62-64.36-155.39z"
                      />
                    </svg>
                  </label>
                </div>
              </div>
            ))}

            <div className="flex justify-end space-x-4">
              <button
                onClick={closeModal}
                className="cursor-pointer btn bg-red-600 w-fit absolute"
                style={{ marginRight: "10px" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                id="lws-submit"
                className="cursor-pointer btn w-fit"
              >
                {editMode ? "Edit" : "Add"}
              </button>
            </div>
          </form>
        </div>
      </main>
    )
  );
};

export default QuizModal;
